import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import useFTPSettings, { FTPGallerySettings } from '@/src/GalleryConfiguration/components/hooks/useFTPSettings';
import { SectionCard, TextInput } from '../../../components/forms';
import { SectionStatus } from './parts';

/* FTP sync — its own save. The password is write-only: never shown,
   empty keeps the stored one (PUT); a typed one goes via POST, like v2. */

interface FtpForm {
    ftpHost: string;
    ftpPort: string;
    ftpUser: string;
    ftpDirectory: string;
    ftpPassword: string;
}

const fromRow = (r: FTPGallerySettings | null): FtpForm => ({
    ftpHost: r?.ftpHost ?? '',
    ftpPort: r?.ftpPort != null ? String(r.ftpPort) : '',
    ftpUser: r?.ftpUser ?? '',
    ftpDirectory: r?.ftpDirectory ?? '',
    ftpPassword: '',
});

const FIELDS: Array<{ k: Exclude<keyof FtpForm, 'ftpPassword'>; l: string; ph: string; mode?: 'numeric' }> = [
    { k: 'ftpHost', l: 'Host', ph: 'ftp.example.com' },
    { k: 'ftpPort', l: 'Port', ph: '21, 22 or 990', mode: 'numeric' },
    { k: 'ftpUser', l: 'Username', ph: '' },
    { k: 'ftpDirectory', l: 'Directory', ph: '/path/to/upload' },
];

type Test = { kind: 'idle' } | { kind: 'running' } | { kind: 'ok'; text: string } | { kind: 'bad'; text: string };

export default function FtpSection({ galleryId, row, loadFailed }: { galleryId: number; row: FTPGallerySettings | null; loadFailed: boolean }) {
    const { saveFTPSettings, saveFTPSettingsWithPassword, listFTPDirectories } = useFTPSettings();
    const [base, setBase] = useState<FtpForm>(() => fromRow(row));
    const [form, setForm] = useState<FtpForm>(base);
    const [state, setState] = useState<'idle' | 'saving' | 'failed' | 'saved'>('idle');
    const [portError, setPortError] = useState<string | null>(null);
    const [test, setTest] = useState<Test>({ kind: 'idle' });

    useEffect(() => {
        const f = fromRow(row);
        setBase(f);
        setForm(f);
    }, [row]);

    const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(base), [form, base]);
    const saving = state === 'saving';

    if (loadFailed) {
        return (
            <SectionCard title="FTP sync">
                <p className="quiet-empty">Couldn’t load FTP settings. Reload the page to try again.</p>
            </SectionCard>
        );
    }

    const edit = (k: keyof FtpForm, v: string) => {
        setForm((f) => ({ ...f, [k]: v }));
        if (k === 'ftpPort') setPortError(null);
        if (!saving) setState('idle');
    };

    const save = async () => {
        if (!dirty || saving) return;
        const port = form.ftpPort.trim();
        if (port && !/^\d{1,5}$/.test(port)) {
            setPortError('Port must be a number, e.g. 21.');
            return;
        }
        setState('saving');
        setTest({ kind: 'idle' });
        const payload = { ...form, galleryId };
        const ok = await (form.ftpPassword ? saveFTPSettingsWithPassword(galleryId, payload) : saveFTPSettings(galleryId, payload)).catch(
            () => false,
        );
        if (ok) {
            const kept = { ...form, ftpPassword: '' };
            setBase(kept);
            setForm(kept);
            setState('saved');
        } else {
            setState('failed');
        }
    };

    /* same call as the classic Test button; it checks the STORED settings */
    const runTest = async () => {
        if (test.kind === 'running') return;
        setTest({ kind: 'running' });
        try {
            const data = await listFTPDirectories(galleryId);
            const dirs = Array.isArray(data) ? data : Array.isArray(data?.directories) ? data.directories : null;
            setTest({
                kind: 'ok',
                text: dirs ? `Connected — ${dirs.length} director${dirs.length === 1 ? 'y' : 'ies'} found.` : 'Connected.',
            });
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.response?.data?.error;
            setTest({ kind: 'bad', text: `Couldn’t connect${typeof msg === 'string' && msg ? ` — ${msg}` : '. Check the host, port and login.'}` });
        }
    };

    return (
        <SectionCard title="FTP sync" action={<SectionStatus state={state} />}>
            <div className="gl-grid2">
                {FIELDS.map((f) => (
                    <label className="field wide" key={f.k}>
                        <span className="field-l">{f.l}</span>
                        <TextInput
                            className="input ev-mono"
                            inputMode={f.mode}
                            placeholder={f.ph}
                            value={form[f.k]}
                            disabled={saving}
                            aria-invalid={f.k === 'ftpPort' && portError ? true : undefined}
                            onChange={(e) => edit(f.k, e.target.value)}
                        />
                        {f.k === 'ftpPort' && portError && <span className="field-e">{portError}</span>}
                    </label>
                ))}
            </div>
            <label className="field wide ev-gap">
                <span className="field-l">Password</span>
                <TextInput
                    type="password"
                    autoComplete="new-password"
                    className="input ev-mono"
                    placeholder="Unchanged unless you type a new one"
                    value={form.ftpPassword}
                    disabled={saving}
                    onChange={(e) => edit('ftpPassword', e.target.value)}
                />
                <span className="field-h">Stored passwords are never shown. Leave empty to keep the current one.</span>
            </label>

            <div className="gl-sec-save">
                <button
                    type="button"
                    className="tb-btn"
                    disabled={test.kind === 'running' || saving || dirty}
                    title={dirty ? 'Save first — the test uses the saved settings' : undefined}
                    onClick={runTest}
                >
                    {test.kind === 'running' ? 'Testing…' : 'Test connection'}
                </button>
                {dirty && (
                    <>
                        <button type="button" className="tb-btn ghost" disabled={saving} onClick={() => { setForm(base); setPortError(null); setState('idle'); }}>
                            Discard
                        </button>
                        <button type="button" className="tb-new" disabled={saving} onClick={save}>
                            {saving ? 'Saving…' : 'Save FTP settings'}
                        </button>
                    </>
                )}
            </div>
            {dirty && <p className="field-h">Save first — the test uses the saved settings.</p>}
            {(test.kind === 'ok' || test.kind === 'bad') && (
                <p className={test.kind === 'ok' ? 'gl-test ok' : 'gl-test bad'} role="status">
                    {test.text}
                </p>
            )}
        </SectionCard>
    );
}
